import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useFeatureCheck, useFeaturedCollections } from '../hooks/useQueries';

interface FeaturedCollection {
  id: number;
  collectionId: number;
  collectionName: string;
  collectionDescription: string;
  displayOrder: number;
  imageCount: number;
}

const FeaturedCollectionsCarousel: React.FC = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const { data: featureData, isLoading: featureLoading } = useFeatureCheck('featured_collections');
  const featureEnabled = featureData?.enabled === true;
  const { data: featuredCollections = [], isLoading: collectionsLoading } = useFeaturedCollections();
  const loading = featureLoading || (featureEnabled && collectionsLoading);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? featuredCollections.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === featuredCollections.length - 1 ? 0 : prev + 1));
  };

  const handleCollectionClick = (collectionId: number) => {
    navigate(`/collections/${collectionId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!featureEnabled || featuredCollections.length === 0) {
    return null;
  }

  const visibleCollections = featuredCollections.slice(currentIndex, currentIndex + 3);
  if (visibleCollections.length < 3 && featuredCollections.length >= 3) {
    visibleCollections.push(...featuredCollections.slice(0, 3 - visibleCollections.length));
  }

  return (
    <Box sx={{ py: 4, px: 2, bgcolor: 'background.paper' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <FolderIcon sx={{ fontSize: 32, color: 'primary.main', mr: 1 }} />
          <Typography variant="h4" component="h2">
            Featured Collections
          </Typography>
        </Box>

        <Box sx={{ position: 'relative' }}>
          {featuredCollections.length > 3 && (
            <>
              <IconButton
                onClick={handlePrevious}
                sx={{
                  position: 'absolute',
                  left: -20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                  bgcolor: 'background.paper',
                  boxShadow: 2,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: -20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                  bgcolor: 'background.paper',
                  boxShadow: 2,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 3,
            }}
          >
            {visibleCollections.map((collection) => (
              <Card
                key={collection.id}
                sx={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={() => handleCollectionClick(collection.collectionId)}
              >
                <CardMedia
                  sx={{
                    height: 200,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FolderIcon sx={{ fontSize: 80, color: 'action.disabled' }} />
                </CardMedia>
                <CardContent>
                  <Typography variant="h6" gutterBottom noWrap>
                    {collection.collectionName}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      mb: 2,
                    }}
                  >
                    {collection.collectionDescription || 'No description available'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      icon={<ImageIcon />}
                      label={`${collection.imageCount} images`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FeaturedCollectionsCarousel;
